import { EventEmitter } from 'eventemitter3';
import { getIosFrameRotation, } from '../../utils/getIosFrameRotation';
import { isUserFacingVideoTrack } from '../../utils/media';
import { isGapDiagnosticsEnabled, logGapDiagnosticSampled, } from './GapDiagnostics';
import { Timekeeper } from './Timekeeper';
import { DEFAULT_MAX_BUFFERED_FRAMES, Track, } from './Track';
import { closeVideoFrame, } from './types/UnencodedVideoFrame';
export class VideoFrameExtractor {
    LOG_PREFIX = '[VideoFrameExtractor]';
    emitter = new EventEmitter();
    track;
    consumer;
    trackProcessor;
    abortController = new AbortController();
    stopped = false;
    outputTrack;
    firstFramePlatformTimestampMicros; // For diagnostics only
    firstFrameRealTimeMs; // performance.now() at first frame
    // Metadata
    frameRate = 0;
    frameNumber = 0n;
    expectedFrameDurationMicros = 0n; // Expected duration based on frame rate
    isScreenSharing = false;
    trackId;
    timestampOffsetMicros;
    getLastTimestampCallback;
    getCurrentTime;
    cachedOrientation;
    // #region agent log
    // Camera-capture cadence: performance.now() of the previous captured
    // frame, for the most-upstream send-side gap probe.
    lastCaptureMs;
    // #endregion
    /**
     * @param trackId the id the output track carries when it differs from
     * the MediaStreamTrack's own: a worker that receives a clone of the
     * page's track keeps publishing under the page's id.
     */
    constructor(track, getLastTimestampCallback, getCurrentTime, trackId) {
        this.track = track;
        this.initializeMetadataFromTrack(track);
        if (trackId !== undefined) {
            this.trackId = trackId;
        }
        this.outputTrack = new Track(trackId ?? track.id, true);
        this.getLastTimestampCallback = getLastTimestampCallback;
        this.getCurrentTime = getCurrentTime ?? (() => performance.now());
    }
    setScreenOrientation(type, angle) {
        this.cachedOrientation = { type, angle };
    }
    doWrite = (frame) => {
        if (this.stopped) {
            frame.close();
            return;
        }
        // #region agent log
        // Most-upstream send-side probe. A gap here means the camera /
        // MediaStreamTrackProcessor delivered no frame (getUserMedia / iOS
        // capture starvation) — upstream of the encoder and of the SAB /
        // main-thread transport entirely. Compare against `videoEncoder.input`
        // and `networkingWorker.videoSend`: if the gap is present here it is
        // capture starvation, not encode/transport.
        const nowCapture = performance.now();
        const captureDeltaMs = this.lastCaptureMs !== undefined
            ? nowCapture - this.lastCaptureMs
            : undefined;
        this.lastCaptureMs = nowCapture;
        logGapDiagnosticSampled('videoFrameExtractor.capture', {
            trackId: this.trackId,
            deltaMs: captureDeltaMs,
        });
        if (isGapDiagnosticsEnabled() &&
            captureDeltaMs !== undefined &&
            captureDeltaMs > 100) {
            console.warn('[capture-spike]', JSON.stringify({
                trackId: this.trackId?.slice(0, 8),
                deltaMs: +captureDeltaMs.toFixed(1),
                ts: Date.now(),
            }));
        }
        // #endregion
        const unencodedVideoFrame = this.createUnencodedVideoFrame(frame);
        if (this.outputTrack.hasListener() &&
            this.outputTrack.getBufferLength() < DEFAULT_MAX_BUFFERED_FRAMES) {
            this.outputTrack.emit(unencodedVideoFrame, false, [
                unencodedVideoFrame.videoFrame,
            ]);
        }
        else {
            closeVideoFrame(unencodedVideoFrame);
        }
    };
    start = () => {
        if (this.track.readyState === 'ended') {
            console.warn(`${this.LOG_PREFIX} track already ended, skipping start`, { trackId: this.trackId });
            this.stopped = true;
            this.outputTrack?.emit(undefined, true);
            this.emitter.emit('aborted');
            return;
        }
        this.consumer = new WritableStream({
            write: (frame) => {
                if (this.timestampOffsetMicros === undefined) {
                    console.log(`${this.LOG_PREFIX} [FIRST_FRAME] capturing timestamp offset`, {
                        trackId: this.trackId?.slice(0, 8),
                    });
                    if (this.getLastTimestampCallback) {
                        console.log(`${this.LOG_PREFIX} [FIRST_FRAME] using getLastTimestampCallback`, {
                            trackId: this.trackId?.slice(0, 8),
                        });
                        // Return the Promise so the WritableStream back-pressures
                        // until the async offset capture completes. Without this,
                        // subsequent frames arrive while the callback is in-flight,
                        // each re-entering this branch and causing out-of-order emission.
                        return this.getLastTimestampCallback()
                            .then((timestamp) => {
                            this.timestampOffsetMicros = timestamp;
                            console.log(`${this.LOG_PREFIX} [FIRST_FRAME] timestamp offset captured`, {
                                trackId: this.trackId?.slice(0, 8),
                                timestampOffsetMs: (Number(timestamp) / 1000).toFixed(1),
                                timestampOffsetMicros: timestamp.toString(),
                            });
                            this.doWrite(frame);
                        })
                            .catch((error) => {
                            console.error(`${this.LOG_PREFIX} getLastTimestampCallback failed, closing frame to prevent resource leak`, {
                                trackId: this.trackId?.slice(0, 8),
                                error,
                            });
                            frame.close();
                        });
                    }
                    this.timestampOffsetMicros = Timekeeper.getInstance('producer:individual').getLastTimestampMicros();
                    this.doWrite(frame);
                }
                else {
                    this.doWrite(frame);
                }
            },
        });
        this.trackProcessor = new MediaStreamTrackProcessor({
            track: this.track,
            maxBufferSize: 1,
        });
        this.trackProcessor.readable
            .pipeTo(this.consumer, {
            signal: this.abortController.signal,
        })
            .catch((_e) => {
            console.log(`${this.LOG_PREFIX} pipeTo canceled`, {
                trackId: this.trackId,
            });
        })
            .finally(() => {
            this.stopped = true;
            console.log(`${this.LOG_PREFIX} pipeline stopped, emitting done`, {
                trackId: this.trackId,
            });
            this.outputTrack?.emit(undefined, true);
            this.emitter.emit('aborted');
        });
    };
    stop = () => {
        console.log(`${this.LOG_PREFIX} stop called`, {
            trackId: this.trackId,
        });
        if (this.stopped) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.abortController.abort('Aborted');
            this.emitter.once('aborted', () => {
                resolve();
            });
        });
    };
    getOutputTrack() {
        return this.outputTrack;
    }
    /**
     * Initialize metadata from the MediaStreamTrack settings.
     */
    initializeMetadataFromTrack(track) {
        this.trackId = track.id;
        const settings = track.getSettings();
        this.frameRate = settings.frameRate ?? 30;
        this.expectedFrameDurationMicros = BigInt(Math.round(1_000_000 / this.frameRate));
        const displaySurface = settings.displaySurface;
        this.isScreenSharing =
            displaySurface === 'monitor' ||
                displaySurface === 'window' ||
                displaySurface === 'browser' ||
                (displaySurface !== undefined && displaySurface !== '');
        console.log(`${this.LOG_PREFIX} initializeMetadataFromTrack`, {
            trackId: this.trackId.slice(0, 8),
            frameRate: this.frameRate,
            settingsWidth: settings.width,
            settingsHeight: settings.height,
            displaySurface: displaySurface ?? 'undefined',
            isScreenSharing: this.isScreenSharing,
        });
    }
    calculateRotation() {
        if (!this.cachedOrientation) {
            return 0;
        }
        return getIosFrameRotation(this.cachedOrientation, isUserFacingVideoTrack(this.track));
    }
    /**
     * Create an UnencodedVideoFrame with all metadata.
     * This method is shared across all implementations.
     */
    createUnencodedVideoFrame(videoFrame) {
        const nowMs = this.getCurrentTime();
        // Capture first-frame anchors
        if (this.firstFrameRealTimeMs === undefined) {
            this.firstFrameRealTimeMs = nowMs;
        }
        if (this.firstFramePlatformTimestampMicros === undefined) {
            this.firstFramePlatformTimestampMicros = BigInt(videoFrame.timestamp);
        }
        if (this.timestampOffsetMicros === undefined) {
            this.timestampOffsetMicros = Timekeeper.getInstance('producer:individual').getLastTimestampMicros();
            console.log(`${this.LOG_PREFIX} [FIRST_FRAME] captured timestamp offset`, {
                trackId: this.trackId?.slice(0, 8),
                timestampOffsetMs: (Number(this.timestampOffsetMicros) / 1000).toFixed(1),
                isScreenSharing: this.isScreenSharing,
                reportedFrameRate: this.frameRate,
            });
        }
        // Compute timestamp using performance.now() (system monotonic clock)
        // instead of videoFrame.timestamp (hardware webcam clock).
        // This eliminates hardware clock drift — the same approach the removed
        // legacy media pipeline used successfully.
        // A/V sync correction is handled downstream in MediaManager, which
        // rebases video timestamps relative to the audio master clock.
        const elapsedMicros = BigInt(Math.round((nowMs - this.firstFrameRealTimeMs) * 1000));
        const timestampMicros = elapsedMicros + this.timestampOffsetMicros;
        const computedRotation = this.calculateRotation();
        const unencodedVideoFrame = {
            frameNumber: this.frameNumber++,
            timestamp: timestampMicros,
            duration: BigInt(videoFrame.duration ?? this.expectedFrameDurationMicros),
            trackId: this.trackId ?? '',
            codedWidth: videoFrame.displayWidth,
            codedHeight: videoFrame.displayHeight,
            isScreenSharing: this.isScreenSharing,
            rotation: computedRotation,
            videoFrame: videoFrame,
            done: false,
            isKeyFrame: true,
            producerWallMillis: Date.now(),
        };
        return unencodedVideoFrame;
    }
}
